import * as bip39 from "bip39";
import type { Seed } from "@breeztech/breez-sdk-spark";

const textEncoder = new TextEncoder();

const STORAGE_KEY = (userId: string) => `mnemonic_${userId}`;
const ENCODED_PREFIX = "v1:";

function getStorageSecret(): string {
  const fromEnv = import.meta.env.VITE_WALLET_MNEMONIC_STORAGE_SECRET;
  if (fromEnv && fromEnv.length > 0) {
    return fromEnv;
  }
  if (import.meta.env.DEV) {
    return "dev-only-mnemonic-storage-secret";
  }
  console.warn(
    "VITE_WALLET_MNEMONIC_STORAGE_SECRET is not set; mnemonic storage uses a weak default."
  );
  return "orange-pill-mnemonic-storage-fallback";
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveAesKey(userId: string): Promise<CryptoKey> {
  const password = textEncoder.encode(`${getStorageSecret()}|${userId}`);
  const salt = textEncoder.encode(`mnemonic-pbkdf2|${userId}`);

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    password,
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 210_000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function encryptMnemonic(userId: string, mnemonic: string): Promise<string> {
  const key = await deriveAesKey(userId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipherBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    textEncoder.encode(mnemonic)
  );
  const combined = new Uint8Array(iv.length + cipherBuf.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(cipherBuf), iv.length);
  return ENCODED_PREFIX + bytesToBase64(combined);
}

async function decryptMnemonic(userId: string, stored: string): Promise<string> {
  const payload = stored.slice(ENCODED_PREFIX.length);
  const combined = base64ToBytes(payload);
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);
  const key = await deriveAesKey(userId);
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );
  return new TextDecoder().decode(plainBuf);
}

function looksLikeLegacyPlaintextMnemonic(raw: string): boolean {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return false;
  return bip39.validateMnemonic(trimmed);
}

export async function getEncryptedMnemonicFromStorage(
  userId: string
): Promise<Seed | null> {
  const raw = localStorage.getItem(STORAGE_KEY(userId));
  if (!raw) {
    return null;
  }

  if (raw.startsWith(ENCODED_PREFIX)) {
    try {
      const mnemonic = await decryptMnemonic(userId, raw);
      return { type: "mnemonic", mnemonic };
    } catch {
      console.error("Failed to decrypt stored mnemonic");
      return null;
    }
  }

  if (looksLikeLegacyPlaintextMnemonic(raw)) {
    const mnemonic = raw.trim().toLowerCase();
    try {
      const encoded = await encryptMnemonic(userId, mnemonic);
      localStorage.setItem(STORAGE_KEY(userId), encoded);
    } catch (e) {
      console.error("Failed to migrate plaintext mnemonic to encrypted storage", e);
    }
    return { type: "mnemonic", mnemonic };
  }

  return null;
}

export async function setEncryptedMnemonicToStorage(
  userId: string,
  mnemonic: string
): Promise<void> {
  if (!mnemonic) {
    localStorage.removeItem(STORAGE_KEY(userId));
    return;
  }
  const encoded = await encryptMnemonic(userId, mnemonic);
  localStorage.setItem(STORAGE_KEY(userId), encoded);
}

export function clearEncryptedMnemonicFromStorage(userId: string): void {
  localStorage.removeItem(STORAGE_KEY(userId));
}
